/* ═══════════════════════════════════════════════════════════════
   Master updates widget — the project's inbox of changes from its
   masters (SYNC_PLAN §2.6). One row per section with updates waiting:
   CSI number · title · change count · where it comes from. Click opens
   that section straight into Review. Read-only: the Accept / Finish
   buttons live in Review (specs.publish, enforced by the server).
   ═══════════════════════════════════════════════════════════════ */

import React from 'react';
import { WidgetFrame, WList } from '../../../components/widgets';
import type { WListRow } from '../../../components/widgets';
import { StatusBadge } from '../../../components/StatusBadge';
import { summarizeBySpec } from '../../../review/feedSummary';
import type { ProjectWidgetCtx, ProjectWidgetDef } from './context';
import { useProjectSyncFeed } from './useProjectSyncFeed';

function MasterUpdatesCard({ ctx }: { ctx: ProjectWidgetCtx }) {
  const { feed, status, error, reload } = useProjectSyncFeed(ctx.project.id);
  const specsById = new Map((ctx.projectSpecs.data || []).map((s) => [s.id, s]));

  const rows: WListRow[] = [...summarizeBySpec(feed).values()]
    .filter((u) => specsById.has(u.specId))
    .sort((a, b) =>
      (specsById.get(a.specId)?.csiNumber || '').localeCompare(
        specsById.get(b.specId)?.csiNumber || ''
      )
    )
    .map((u) => {
      const s = specsById.get(u.specId)!;
      const meta = [...u.from, ...u.waitingAt.map((w) => `waiting upstream at ${w}`)].join(' · ');
      return {
        id: u.specId,
        lead: (
          <span className="inline-block min-w-[8ch] font-mono text-xs font-semibold text-primary">
            {s.csiNumber || '—'}
          </span>
        ),
        // Title keeps a floor and the source note yields: at half width a
        // long meta used to push the title to zero width.
        primary: (
          <span className="flex min-w-0 items-baseline gap-2">
            <span className="min-w-[12ch] truncate">{s.title || 'Untitled'}</span>
            {meta && <span className="min-w-0 flex-1 truncate text-sm text-muted">{meta}</span>}
          </span>
        ),
        trailing:
          u.count > 0 ? (
            <StatusBadge
              status="updates"
              label={`${u.count} ${u.count === 1 ? 'change' : 'changes'}`}
              variant="info"
            />
          ) : undefined,
        onClick: () =>
          ctx.onOpenProjectSpec?.(s.id, ctx.project.id, u.count > 0 ? { review: true } : undefined),
      };
    });

  const total = rows.length;
  return (
    <WidgetFrame
      title="Master updates"
      form="list"
      status={status === 'ready' ? (total ? 'ready' : 'empty') : status}
      meta={total ? `${total} section${total === 1 ? '' : 's'}` : undefined}
      empty={{ title: 'Everything is up to date with your masters.' }}
      error={{ message: error || 'Could not load master updates', onRetry: reload }}
    >
      <WList rows={rows} max={Math.max(total, 1)} />
    </WidgetFrame>
  );
}

export const masterUpdatesWidget: ProjectWidgetDef = {
  id: 'master_updates',
  title: 'Master updates',
  description: 'Changes from your masters waiting for review, by section.',
  category: 'overview',
  requires: ['project_specs.view'],
  inputSurface: false,
  cols: { min: 6, max: 12 },
  defaultSize: 'standard',
  render: (ctx) => <MasterUpdatesCard ctx={ctx} />,
};
